import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Save, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { settingsApi } from "@/lib/crmApi";

const DAYS = [
  { idx: 0, label: "Lun" },
  { idx: 1, label: "Mar" },
  { idx: 2, label: "Mer" },
  { idx: 3, label: "Jeu" },
  { idx: 4, label: "Ven" },
  { idx: 5, label: "Sam" },
  { idx: 6, label: "Dim" },
];

const SLOT_DURATIONS = [15, 30, 45, 60, 90, 120];

export default function BookingSettingsPanel({ projetId = null, projetNom = "" }) {
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5]);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(18);
  const [slotMin, setSlotMin] = useState(60);
  const [lookahead, setLookahead] = useState(14);
  const [publicUrl, setPublicUrl] = useState("");
  const [initial, setInitial] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Per-project booking config: read the merged "effective" view so the
    // panel shows what this project actually uses (its overrides on top of
    // the global defaults). Falls back to the global singleton when no
    // project is active.
    const fetcher = projetId
      ? settingsApi.getForProject(projetId).then((r) => r.effective || {})
      : settingsApi.get();
    fetcher
      .then((s) => {
        if (cancelled) return;
        const en = s.booking_enabled !== false;
        const d = Array.isArray(s.booking_working_days)
          ? s.booking_working_days
          : [0, 1, 2, 3, 4, 5];
        const sh = Number(s.booking_start_hour ?? 9);
        const eh = Number(s.booking_end_hour ?? 18);
        const sm = Number(s.booking_slot_duration_min ?? 60);
        const la = Number(s.booking_lookahead_days ?? 14);
        const pu = s.booking_public_url || "https://crm.sogi.sn";
        setEnabled(en);
        setDays(d);
        setStartHour(sh);
        setEndHour(eh);
        setSlotMin(sm);
        setLookahead(la);
        setPublicUrl(pu);
        setInitial({ en, d, sh, eh, sm, la, pu });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projetId]);

  const dirty =
    enabled !== initial.en ||
    JSON.stringify(days) !== JSON.stringify(initial.d) ||
    startHour !== initial.sh ||
    endHour !== initial.eh ||
    slotMin !== initial.sm ||
    lookahead !== initial.la ||
    publicUrl !== initial.pu;

  const toggleDay = (idx) => {
    setDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()
    );
  };

  const save = async () => {
    if (startHour >= endHour) {
      toast.error("Heure de fin doit être > heure de début");
      return;
    }
    if (days.length === 0) {
      toast.error("Au moins un jour doit être coché");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        booking_enabled: enabled,
        booking_working_days: days,
        booking_start_hour: startHour,
        booking_end_hour: endHour,
        booking_slot_duration_min: slotMin,
        booking_lookahead_days: lookahead,
        booking_public_url: publicUrl.trim(),
      };
      // Per-project save when a project is active; else edit the global
      // defaults. The per-project endpoint returns {effective, overrides} —
      // normalise to a flat dict like the global one.
      const res = projetId
        ? await settingsApi.updateForProject(projetId, payload)
        : await settingsApi.update(payload);
      const s = projetId ? (res.effective || {}) : res;
      setInitial({
        en: s.booking_enabled !== false,
        d: s.booking_working_days,
        sh: s.booking_start_hour,
        eh: s.booking_end_hour,
        sm: s.booking_slot_duration_min,
        la: s.booking_lookahead_days,
        pu: s.booking_public_url,
      });
      toast.success("Disponibilités enregistrées");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
      data-testid="section-booking-settings"
    >
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
          <CalendarCheck className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold">
            Prise de RDV en ligne
            {projetNom && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {projetNom}
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground">
            {projetNom
              ? `Disponibilités spécifiques au projet ${projetNom}. Les leads de ce projet voient ces créneaux.`
              : "Tes leads choisissent eux-mêmes leur créneau via un lien WhatsApp. Configure tes disponibilités ici."}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={loading}
          data-testid="booking-toggle"
        />
      </div>

      <fieldset disabled={!enabled || loading} className={!enabled ? "opacity-50" : ""}>
        {/* Working days */}
        <div className="mb-5 space-y-2">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Jours travaillés
          </Label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const active = days.includes(d.idx);
              return (
                <button
                  key={d.idx}
                  type="button"
                  onClick={() => toggleDay(d.idx)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                    active
                      ? "border-emerald-500 bg-emerald-500 text-white dark:bg-emerald-600"
                      : "border-border/60 bg-card hover:border-emerald-400"
                  }`}
                  data-testid={`booking-day-${d.idx}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Hours */}
        <div className="mb-5 grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="bk-start" className="text-[11px]">Heure de début</Label>
            <Input
              id="bk-start"
              type="number"
              min="0"
              max="23"
              value={startHour}
              onChange={(e) => setStartHour(Number(e.target.value))}
            />
            <p className="text-[10px] text-muted-foreground">Premier slot du jour</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-end" className="text-[11px]">Heure de fin</Label>
            <Input
              id="bk-end"
              type="number"
              min="1"
              max="23"
              value={endHour}
              onChange={(e) => setEndHour(Number(e.target.value))}
            />
            <p className="text-[10px] text-muted-foreground">Dernier slot avant cette heure</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-slot" className="text-[11px]">Durée d'un RDV</Label>
            <select
              id="bk-slot"
              value={slotMin}
              onChange={(e) => setSlotMin(Number(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {SLOT_DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {d} min
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Lookahead */}
        <div className="mb-5 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="bk-look" className="text-[11px]">
              Fenêtre de réservation (jours)
            </Label>
            <Input
              id="bk-look"
              type="number"
              min="1"
              max="60"
              value={lookahead}
              onChange={(e) => setLookahead(Number(e.target.value))}
            />
            <p className="text-[10px] text-muted-foreground">
              Le lead voit les créneaux libres sur les {lookahead} prochains jours
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-url" className="text-[11px] flex items-center gap-1">
              <Globe className="h-3 w-3" /> URL publique du CRM
            </Label>
            <Input
              id="bk-url"
              type="url"
              value={publicUrl}
              onChange={(e) => setPublicUrl(e.target.value)}
              placeholder="https://crm.sogi.sn"
            />
            <p className="text-[10px] text-muted-foreground">
              Sert à construire le lien envoyé au lead
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            Aperçu
          </p>
          <p className="mt-1 text-xs">
            Les leads pourront choisir parmi <b>{days.length} jours</b> par semaine,
            de <b>{startHour}h</b> à <b>{endHour}h</b>, avec des créneaux de{" "}
            <b>{slotMin} min</b>, sur les <b>{lookahead} prochains jours</b>.
          </p>
          <p className="mt-2 text-[10px] text-muted-foreground">
            Lien type : {publicUrl.replace(/\/+$/, "")}/booking/&lt;token-aleatoire&gt;
          </p>
        </div>
      </fieldset>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving || loading}
          className="gap-2"
          data-testid="booking-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </section>
  );
}
